import React, { useState, useEffect, useRef } from "react";
import { ref, push, onValue, update } from "firebase/database";
import { database } from "../../firebase";
import {
    Box,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Paper,
    TextField,
    Typography,
    useMediaQuery,
    Avatar,
    Divider,
    CircularProgress,
    Snackbar,
    Alert,
    Container,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";

import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SendIcon from "@mui/icons-material/Send";
import Sidebar from "../Sidebar/SidebarAdmin"; // Adjust path as needed

const ChatAdmin = () => {
    // Colors for styling
    const primaryColor = "#00bcd4"; // Used for header, avatar background, etc.
    const secondaryColor = "#A8E9F3"; // Accent color

    // Sidebar toggling
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);
    const sidebarWidth = isSidebarOpen ? 250 : 80;


    

    // Utility function: sanitize an email to use as Firebase keys
    const sanitizePath = (email) =>
        email.replace(/[@.]/g, (match) => (match === "@" ? "_AT_" : "_DOT_"));

    const formatDateTime = (timestamp) =>
        new Date(timestamp).toLocaleString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });

    const getInitials = (name) =>
        name
            .split(" ")
            .map((word) => word[0])
            .join("")
            .toUpperCase();

    const AdminChatAdmin = () => {
        const currentUser = "Nobunaga-2001";

        const [users, setUsers] = useState([]);
        const [selectedUser, setSelectedUser] = useState(null);
        const [messages, setMessages] = useState([]);
        const [input, setInput] = useState("");
        const [loadingMessages, setLoadingMessages] = useState(false);
        const [error, setError] = useState(null);
        const [snackbar, setSnackbar] = useState({
            open: false,
            message: "",
            severity: "info",
        });

        // Filter: "all", "read", "unread"
        const [filter, setFilter] = useState("all");
        const [filterDate, setFilterDate] = useState("");

        // Unread message counts
        const [unreadCounts, setUnreadCounts] = useState({});
        // Entire ChatAdminRooms data (so we can read lastUpdated)
        const [ChatAdminRooms, setChatAdminRooms] = useState({});

        // sort filteredUsers by ChatAdminRooms[lastUpdated] desc

        // Refs and layout logic
        const messagesRefContainer = useRef(null);
        const conversationListRef = useRef(null);
        const [listWidth, setListWidth] = useState(320);
        const isMobile = useMediaQuery("(max-width:960px)");
        const showDropdown = listWidth < 250 || isMobile;
        const MARGIN_TOP = 10;

        // ===== 1. FETCH USERS =====
        useEffect(() => {
            const usersRef = ref(database, "users");
            const unsubscribe = onValue(usersRef, (snapshot) => {
                try {
                    const data = snapshot.val();
                    if (data) {
                        const usersArray = Object.entries(data).map(
                            ([id, user]) => ({
                                id,
                                ...user,
                            })
                        );
                        // Sort by first name initially (you can remove this if you want)
                        usersArray.sort((a, b) =>
                            a.first_name.localeCompare(b.first_name)
                        );
                        setUsers(usersArray);
                    }
                } catch (err) {
                    console.error("Error fetching users:", err);
                    setSnackbar({
                        open: true,
                        message:
                            "Error loading users. Please refresh the page.",
                        severity: "error",
                    });
                }
            });
            return () => unsubscribe();
        }, []);

        // ===== 2. LISTEN FOR ChatAdminROOMS (UNREAD COUNTS + LAST UPDATED) =====
        useEffect(() => {
            const ChatAdminRoomsRef = ref(database, "ChatAdminRooms");
            const unsubscribe = onValue(ChatAdminRoomsRef, (snapshot) => {
                const data = snapshot.val() || {};
                setChatAdminRooms(data);

                // Build unread counts
                const counts = {};
                Object.entries(data).forEach(([sanitizedUserId, room]) => {
                    if (room.messages) {
                        const unread = Object.values(room.messages).filter(
                            (msg) =>
                                msg.sender !== currentUser &&
                                msg.status === "sent"
                        ).length;
                        counts[sanitizedUserId] = unread;
                    }
                });
                setUnreadCounts(counts);
            });
            return () => unsubscribe();
        }, [currentUser]);

        // ===== 3. SORT USERS BY lastUpdated DESCENDING, THEN FILTER =====
        const getSortedUsers = () => {
            if (!users) return [];
            // Clone
            const userArray = [...users];
            // For each user, attach lastUpdated from ChatAdminRooms if available
            userArray.forEach((u) => {
                const userKey = sanitizePath(u.email);
                if (
                    ChatAdminRooms[userKey] &&
                    ChatAdminRooms[userKey].lastUpdated
                ) {
                    u.lastUpdated = ChatAdminRooms[userKey].lastUpdated;
                } else {
                    // If there's no ChatAdmin room yet, set it to the earliest date so it sorts last
                    u.lastUpdated = "1970-01-01T00:00:00Z";
                }
            });
            // Sort by lastUpdated (descending)
            userArray.sort(
                (a, b) => new Date(b.lastUpdated) - new Date(a.lastUpdated)
            );
            return userArray;
        };

        const filterUsers = () => {
            let sortedUsers = getSortedUsers();

            if (filter === "all") return sortedUsers;
            if (filter === "unread") {
                return sortedUsers.filter((u) => {
                    const userKey = sanitizePath(u.email);
                    return unreadCounts[userKey] && unreadCounts[userKey] > 0;
                });
            }
            if (filter === "read") {
                return sortedUsers.filter((u) => {
                    const userKey = sanitizePath(u.email);
                    return (
                        !unreadCounts[userKey] || unreadCounts[userKey] === 0
                    );
                });
            }
            return sortedUsers;
        };

        

        const filteredUsers = filterUsers();

        // ===== 4. FETCH MESSAGES FOR SELECTED USER =====
        useEffect(() => {
            if (selectedUser) {
                setLoadingMessages(true);
                const sanitizedID = sanitizePath(selectedUser);
                const messagesRef = ref(
                    database,
                    `ChatAdminRooms/${sanitizedID}/messages`
                );
                const unsubscribe = onValue(messagesRef, (snapshot) => {
                    try {
                        const data = snapshot.val();
                        let arr = data
                            ? Object.entries(data).map(([id, msg]) => ({
                                  id,
                                  ...msg,
                              }))
                            : [];
                        arr.sort(
                            (a, b) =>
                                new Date(a.timestamp) - new Date(b.timestamp)
                        );
                        setMessages(arr);
                    } catch (err) {
                        console.error("Error loading messages:", err);
                        setSnackbar({
                            open: true,
                            message:
                                "Error loading messages. Please try again.",
                            severity: "error",
                        });
                    } finally {
                        setLoadingMessages(false);
                    }
                });
                return () => unsubscribe();
            }
        }, [selectedUser]);
// sort filteredUsers by ChatAdminRooms[lastUpdated] desc
const sortedUsers = React.useMemo(() => {
    return filteredUsers
      .map(u => {
        const key = sanitizePath(u.email);
        return {
          ...u,
          _lastUpdated: ChatAdminRooms[key]?.lastUpdated ?? "1970-01-01T00:00:00Z",
        };
      })
      .sort(
        (a, b) =>
          new Date(b._lastUpdated) - new Date(a._lastUpdated)
      );
  }, [filteredUsers, ChatAdminRooms]);
  
        // ===== 5. AUTO-SCROLL MESSAGES =====
        useEffect(() => {
            if (messagesRefContainer.current) {
                const el = messagesRefContainer.current;
                el.scrollTop = el.scrollHeight;
            }
        }, [messages]);

        // ===== 6. MARK UNREAD AS READ WHEN VIEWING =====
        useEffect(() => {
            if (selectedUser) {
                const unreadMessages = messages.filter(
                    (m) => m.sender !== currentUser && m.status === "sent"
                );
                unreadMessages.forEach((m) => {
                    const sanitizedID = sanitizePath(selectedUser);
                    const msgRef = ref(
                        database,
                        `ChatAdminRooms/${sanitizedID}/messages/${m.id}`
                    );
                    update(msgRef, { status: "read" });
                });
            }
        }, [messages, selectedUser]);

        // ===== 7. SEND MESSAGE =====
        const handleSend = async () => {
            if (!input.trim() || !selectedUser) return;
            const text = input.trim();
            setInput("");
            try {
                const sanitizedID = sanitizePath(selectedUser);
                const ChatAdminRef = ref(
                    database,
                    `ChatAdminRooms/${sanitizedID}`
                );

                // Update lastUpdated whenever we send a message
                await update(ChatAdminRef, {
                    participants: { user: selectedUser, admin: currentUser },
                    lastUpdated: new Date().toISOString(),
                });

                const messagesRef = ref(
                    database,
                    `ChatAdminRooms/${sanitizedID}/messages`
                );
                const msgData = {
                    text,
                    sender: currentUser,
                    timestamp: new Date().toISOString(),
                    status: "sent",
                };
                await push(messagesRef, msgData);
            } catch (err) {
                console.error("Error sending message:", err);
                setSnackbar({
                    open: true,
                    message: "Failed to send message. Please try again.",
                    severity: "error",
                });
                setInput(text);
            }
        };

        // ===== 8. RESIZE OBSERVER FOR CONVERSATION LIST =====
        useEffect(() => {
            if (!conversationListRef.current) return;
            const ro = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    setListWidth(entry.contentRect.width);
                }
            });
            ro.observe(conversationListRef.current);
            return () => ro.disconnect();
        }, []);

        return (
            <>
                {/* Sidebar */}
                <Sidebar
                    isSidebarOpen={isSidebarOpen}
                    toggleSidebar={toggleSidebar}
                />

                {/* Main Container */}
                <div
                    style={{
                        marginLeft:  isMobile ? 0 : `${sidebarWidth}px`,
                        width:  isMobile ? '100%' : `calc(100% - ${sidebarWidth}px)`,
                        transition: "margin-left 0.3s ease",
            
                    }}
                >
                    <Box
                        sx={{
                            backgroundColor: "#f0f2f5",
                            minHeight: "100vh",
                            display: "flex",
                            flexDirection: "column",
                            fontFamily: "'Montserrat', sans-serif",
                        }}
                    >
                        <Container
                            maxWidth="xl"
                            sx={{
                                mt: `${MARGIN_TOP}px`,
                                flexGrow: 1,
                                display: "flex",
                                flexDirection: "column",
                                height: `calc(100vh - ${MARGIN_TOP}px)`,
                                p: { xs: 1, sm: 2, md: 3 },
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: { xs: "column", md: "row" },
                                    gap: 2,
                                    height: "100%",
                                }}
                            >
                                {/* LEFT: Conversation List Panel */}
                                <Paper
                                    elevation={3}
                                    ref={conversationListRef}
                                    sx={{
                                        width: { xs: "100%", md: 320 },
                                        height: {
                                            xs: isMobile ? "40vh" : "100%",
                                            md: "100%",
                                        },
                                        display: "flex",
                                        flexDirection: "column",
                                        overflow: "hidden",
                                    }}
                                >
                                    {/* Header */}
                                    <Box
                                        sx={{
                                            p: 2,
                                            backgroundColor: primaryColor,
                                            color: "white",
                                        }}
                                    >
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontFamily:
                                                    "'Montserrat', sans-serif",
                                            }}
                                        >
                                            Conversations (
                                            {filteredUsers.length})
                                        </Typography>
                                    </Box>

                                    {/* Filter Area */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            p: 2,
                                            backgroundColor: "#fff",
                                            gap: 1,
                                        }}
                                    >
                                        {showDropdown ? (
                                            <FormControl
                                                variant="outlined"
                                                size="small"
                                                sx={{
                                                    minWidth: 100,
                                                    maxWidth: 120,
                                                    ml: 1,
                                                }}
                                            >
                                                <InputLabel
                                                    sx={{
                                                        fontFamily:
                                                            "'Montserrat', sans-serif",
                                                    }}
                                                >
                                                    Filter
                                                </InputLabel>
                                                <Select
                                                    label="Filter"
                                                    value={filter}
                                                    onChange={(e) =>
                                                        setFilter(
                                                            e.target.value
                                                        )
                                                    }
                                                    sx={{
                                                        fontFamily:
                                                            "'Montserrat', sans-serif",
                                                        fontSize: "0.85rem",
                                                    }}
                                                >
                                                    <MenuItem
                                                        value="all"
                                                        sx={{
                                                            fontFamily:
                                                                "'Montserrat', sans-serif",
                                                        }}
                                                    >
                                                        All
                                                    </MenuItem>
                                                    <MenuItem
                                                        value="read"
                                                        sx={{
                                                            fontFamily:
                                                                "'Montserrat', sans-serif",
                                                        }}
                                                    >
                                                        Read
                                                    </MenuItem>
                                                    <MenuItem
                                                        value="unread"
                                                        sx={{
                                                            fontFamily:
                                                                "'Montserrat', sans-serif",
                                                        }}
                                                    >
                                                        Unread
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>
                                        ) : (
                                            <Box
                                                sx={{ display: "flex", gap: 1 }}
                                            >
                                                <Box
                                                    onClick={() =>
                                                        setFilter("all")
                                                    }
                                                    sx={{
                                                        backgroundColor:
                                                            filter === "all"
                                                                ? "#4e4e4e"
                                                                : "#757575",
                                                        color: "#fff",
                                                        borderRadius: 2,
                                                        px: 2,
                                                        py: 0.5,
                                                        cursor: "pointer",
                                                        fontFamily:
                                                            "'Montserrat', sans-serif",
                                                        fontWeight: 600,
                                                        fontSize: "0.9rem",
                                                        "&:hover": {
                                                            backgroundColor:
                                                                "#4e4e4e",
                                                        },
                                                    }}
                                                >
                                                    All
                                                </Box>
                                                <Box
                                                    onClick={() =>
                                                        setFilter("read")
                                                    }
                                                    sx={{
                                                        backgroundColor:
                                                            filter === "read"
                                                                ? "#0098A6"
                                                                : "#00bcd4",
                                                        color: "#fff",
                                                        borderRadius: 2,
                                                        px: 2,
                                                        py: 0.5,
                                                        cursor: "pointer",
                                                        fontFamily:
                                                            "'Montserrat', sans-serif",
                                                        fontWeight: 600,
                                                        fontSize: "0.9rem",
                                                        "&:hover": {
                                                            backgroundColor:
                                                                "#0098A6",
                                                        },
                                                    }}
                                                >
                                                    Read
                                                </Box>
                                                <Box
                                                    onClick={() =>
                                                        setFilter("unread")
                                                    }
                                                    sx={{
                                                        backgroundColor:
                                                            filter === "unread"
                                                                ? "#c62828"
                                                                : "#f44336",
                                                        color: "#fff",
                                                        borderRadius: 2,
                                                        px: 2,
                                                        py: 0.5,
                                                        cursor: "pointer",
                                                        fontFamily:
                                                            "'Montserrat', sans-serif",
                                                        fontWeight: 600,
                                                        fontSize: "0.9rem",
                                                        "&:hover": {
                                                            backgroundColor:
                                                                "#c62828",
                                                        },
                                                    }}
                                                >
                                                    Unread
                                                </Box>
                                            </Box>
                                        )}

                                        {/* Calendar Icon with Invisible Date Input */}
                                       
                                    </Box>

                                    <Divider />

                                    {/* Scrollable Conversation List */}
                                    <List
                                        sx={{
                                            flexGrow: 1,
                                            overflow: "auto",
                                            "&::-webkit-scrollbar": {
                                                width: "6px",
                                            },
                                            "&::-webkit-scrollbar-thumb": {
                                                backgroundColor:
                                                    "rgba(0,0,0,0.2)",
                                                borderRadius: "3px",
                                            },
                                        }}
                                    >
                                        {sortedUsers.map((user) => (
                                        <ListItem key={user.id} disablePadding>
                                            <ListItemButton
                                            selected={selectedUser === user.email}
                                            onClick={() => setSelectedUser(user.email)}
                                            sx={{
                                                "&.Mui-selected": {
                                                backgroundColor: "rgba(25, 118, 210, 0.08)",
                                                },
                                                "&:hover": {
                                                backgroundColor: "rgba(25, 118, 210, 0.04)",
                                                },
                                            }}
                                            >
                                            <Avatar
                                                sx={{
                                                mr: 2,
                                                bgcolor: primaryColor,
                                                width: 40,
                                                height: 40,
                                                fontFamily: "'Montserrat', sans-serif",
                                                }}
                                            >
                                                {getInitials(`${user.first_name} ${user.last_name}`)}
                                            </Avatar>
                                            <ListItemText
                                                primary={`${user.first_name} ${user.last_name}`}
                                                secondary={user.email}
                                                primaryTypographyProps={{
                                                fontSize: "0.95rem",
                                                fontWeight:
                                                    (unreadCounts[sanitizePath(user.email)] || 0) > 0
                                                    ? "bold"
                                                    : 400,
                                                fontFamily: "'Montserrat', sans-serif",
                                                }}
                                                secondaryTypographyProps={{
                                                fontSize: "0.8rem",
                                                noWrap: true,
                                                fontFamily: "'Montserrat', sans-serif",
                                                }}
                                            />
                                            </ListItemButton>
                                        </ListItem>
                                        ))}

                                    </List>
                                </Paper>

                                {/* RIGHT: ChatAdmin Area */}
                                <Paper
                                    elevation={3}
                                    sx={{
                                        flexGrow: 1,
                                        display: "flex",
                                        flexDirection: "column",
                                        height: {
                                            xs: isMobile ? "55vh" : "100%",
                                            md: "100%",
                                        },
                                        marginBottom: {
                                            xs: '70px',
                                            md:0,
                                        },
                                 
                                        overflow: "hidden",
                                    }}
                                >
                                    {selectedUser ? (
                                        <>
                                            {/* ChatAdmin Header */}
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    backgroundColor: primaryColor,
                                                    color: "white",
                                                    display: "flex",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <Avatar
                                                    sx={{
                                                        mr: 2,
                                                        bgcolor: secondaryColor,
                                                        width: 40,
                                                        height: 40,
                                                        fontFamily:
                                                            "'Montserrat', sans-serif",
                                                    }}
                                                >
                                                    {getInitials(
                                                        users.find(
                                                            (u) =>
                                                                u.email ===
                                                                selectedUser
                                                        )?.first_name || ""
                                                    )}
                                                </Avatar>
                                                <Box
                                                    sx={{ overflow: "hidden" }}
                                                >
                                                    <Typography
                                                        variant="h6"
                                                        noWrap
                                                        sx={{
                                                            fontFamily:
                                                                "'Montserrat', sans-serif",
                                                        }}
                                                    >
                                                        {
                                                            users.find(
                                                                (u) =>
                                                                    u.email ===
                                                                    selectedUser
                                                            )?.first_name
                                                        }{" "}
                                                        {
                                                            users.find(
                                                                (u) =>
                                                                    u.email ===
                                                                    selectedUser
                                                            )?.last_name
                                                        }
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            opacity: 0.8,
                                                            fontFamily:
                                                                "'Montserrat', sans-serif",
                                                        }}
                                                        noWrap
                                                    >
                                                        {selectedUser}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* ChatAdmin Messages */}
                                            <Box
                                                ref={messagesRefContainer}
                                                sx={{
                                                    flexGrow: 1,
                                                    overflowY: "auto",
                                                    p: 2,
                                                    backgroundColor: "#f8f9fa",
                                                    "&::-webkit-scrollbar": {
                                                        width: "6px",
                                                    },
                                                    "&::-webkit-scrollbar-thumb": {
                                                        backgroundColor:
                                                            "rgba(0,0,0,0.2)",
                                                        borderRadius: "3px",
                                                    },
                                                    fontFamily:
                                                        "'Montserrat', sans-serif",
                                                }}
                                            >
                                                {loadingMessages ? (
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            justifyContent:
                                                                "center",
                                                            p: 4,
                                                        }}
                                                    >
                                                        <CircularProgress />
                                                    </Box>
                                                ) : messages.length > 0 ? (
                                                    messages.map((message) => (
                                                        <Box
                                                            key={message.id}
                                                            sx={{
                                                                display: "flex",
                                                                justifyContent:
                                                                    message.sender ===
                                                                    currentUser
                                                                        ? "flex-end"
                                                                        : "flex-start",
                                                                mb: 2,
                                                            }}
                                                        >
                                                            <Paper
                                                                elevation={1}
                                                                sx={{
                                                                    p: 2,
                                                                    backgroundColor:
                                                                        message.sender ===
                                                                        currentUser
                                                                            ? primaryColor
                                                                            : "white",
                                                                    color:
                                                                        message.sender ===
                                                                        currentUser
                                                                            ? "white"
                                                                            : "inherit",
                                                                    maxWidth: {
                                                                        xs:
                                                                            "85%",
                                                                        sm:
                                                                            "70%",
                                                                    },
                                                                    borderRadius:
                                                                        message.sender ===
                                                                        currentUser
                                                                            ? "20px 20px 0 20px"
                                                                            : "20px 20px 20px 0",
                                                                    fontFamily:
                                                                        "'Montserrat', sans-serif",
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="body1"
                                                                    sx={{
                                                                        wordBreak:
                                                                            "break-word",
                                                                        whiteSpace:
                                                                            "pre-wrap",
                                                                        fontFamily:
                                                                            "'Montserrat', sans-serif",
                                                                        // BOLD IF status is 'sent' from a user
                                                                        fontWeight:
                                                                            message.sender !==
                                                                                currentUser &&
                                                                            message.status ===
                                                                                "sent"
                                                                                ? "bold"
                                                                                : "normal",
                                                                    }}
                                                                >
                                                                    {
                                                                        message.text
                                                                    }
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        display:
                                                                            "block",
                                                                        mt: 0.5,
                                                                        opacity: 0.8,
                                                                        fontSize:
                                                                            "0.7rem",
                                                                        textAlign:
                                                                            message.sender ===
                                                                            currentUser
                                                                                ? "right"
                                                                                : "left",
                                                                        fontFamily:
                                                                            "'Montserrat', sans-serif",
                                                                    }}
                                                                >
                                                                    {formatDateTime(
                                                                        message.timestamp
                                                                    )}
                                                                </Typography>
                                                            </Paper>
                                                        </Box>
                                                    ))
                                                ) : (
                                                    <Box
                                                        sx={{
                                                            textAlign: "center",
                                                            p: 4,
                                                            color:
                                                                "text.secondary",
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                fontFamily:
                                                                    "'Montserrat', sans-serif",
                                                            }}
                                                        >
                                                            No messages yet.
                                                            Start the
                                                            conversation!
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>

                                            {/* Message Input */}
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    backgroundColor: "white",
                                                    borderTop: 1,
                                                    borderColor: "divider",
                                                    fontFamily:
                                                        "'Montserrat', sans-serif",
                                                }}
                                            >
                                                <TextField
                                                    fullWidth
                                                    placeholder="Type your message..."
                                                    value={input}
                                                    onChange={(e) => {
                                                        setInput(
                                                            e.target.value
                                                        );
                                                        if (error)
                                                            setError(null);
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (
                                                            e.key === "Enter" &&
                                                            !e.shiftKey
                                                        ) {
                                                            e.preventDefault();
                                                            handleSend();
                                                        }
                                                    }}
                                                    multiline
                                                    maxRows={4}
                                                    variant="outlined"
                                                    error={!!error}
                                                    helperText={error}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={
                                                                        handleSend
                                                                    }
                                                                    disabled={
                                                                        !input.trim()
                                                                    }
                                                                    sx={{
                                                                        backgroundColor: input.trim()
                                                                            ? primaryColor
                                                                            : "transparent",
                                                                        color: input.trim()
                                                                            ? "white"
                                                                            : "inherit",
                                                                        "&:hover": {
                                                                            backgroundColor: input.trim()
                                                                                ? secondaryColor
                                                                                : "transparent",
                                                                        },
                                                                        "&:disabled": {
                                                                            backgroundColor:
                                                                                "transparent",
                                                                        },
                                                                    }}
                                                                >
                                                                    <SendIcon />
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    sx={{
                                                        "& .MuiOutlinedInput-root": {
                                                            borderRadius: 2,
                                                        },
                                                        fontFamily:
                                                            "'Montserrat', sans-serif",
                                                    }}
                                                />
                                            </Box>
                                        </>
                                    ) : (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                height: "100%",
                                                p: 4,
                                                backgroundColor: "#f8f9fa",
                                                fontFamily:
                                                    "'Montserrat', sans-serif",
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                color="text.secondary"
                                                align="center"
                                                sx={{
                                                    fontFamily:
                                                        "'Montserrat', sans-serif",
                                                }}
                                            >
                                                Select a user to start
                                                ChatAdminting
                                            </Typography>
                                        </Box>
                                    )}
                                </Paper>
                            </Box>
                        </Container>

                        <Snackbar
                            open={snackbar.open}
                            autoHideDuration={6000}
                            onClose={() =>
                                setSnackbar({ ...snackbar, open: false })
                            }
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "center",
                            }}
                        >
                            <Alert
                                onClose={() =>
                                    setSnackbar({ ...snackbar, open: false })
                                }
                                severity={snackbar.severity}
                                sx={{
                                    width: "100%",
                                    fontFamily: "'Montserrat', sans-serif",
                                }}
                            >
                                {snackbar.message}
                            </Alert>
                        </Snackbar>
                    </Box>
                </div>
            </>
        );
    };

    return <AdminChatAdmin />;
};

export default ChatAdmin;
